import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SignInButton, SignUpButton, UserButton, useUser } from '@clerk/clerk-react';
const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Button asChild variant="ghost" className="text-brand-dark dark:text-amber-50 hover:bg-brand-accent/10 dark:hover:bg-neutral-800">
    <Link to={to}>{children}</Link>
  </Button>
);
const MobileNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <SheetClose asChild>
    <Link to={to} className="block text-lg py-2 text-brand-dark dark:text-amber-50 hover:text-brand-accent">
      {children}
    </Link>
  </SheetClose>
);
export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isSignedIn } = useUser();

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50 bg-brand-parchment/80 dark:bg-neutral-900/80 backdrop-blur-lg border-b border-brand-accent/20 dark:border-neutral-700 transition-colors duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <BookOpen className="h-8 w-8 text-brand-dark dark:text-amber-50" />
            <span className="font-serif text-2xl font-bold text-brand-dark dark:text-amber-50">
              NovellaStream
            </span>
          </Link>
          <nav className="hidden md:flex items-center space-x-2">
            <NavLink to="/app/workspace">Workspace</NavLink>
            <NavLink to="/browse">Browse</NavLink>
            <NavLink to="/app/library">My Library</NavLink>
            {/* Settings disabled - webhook config is admin-only */}
            {/* <NavLink to="/app/settings">Settings</NavLink> */}
            <NavLink to="/pricing">Pricing</NavLink>
            <ThemeToggle className="" />
            {isSignedIn ? (
              <UserButton />
            ) : (
              <>
                <SignInButton mode="modal">
                  <Button variant="ghost" className="text-brand-dark dark:text-amber-50 hover:bg-brand-accent/10 dark:hover:bg-neutral-800">
                    Login
                  </Button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <Button className="bg-brand-dark dark:bg-amber-600 text-brand-parchment dark:text-white hover:bg-brand-dark/90 dark:hover:bg-amber-700 rounded-full px-5">
                    Sign Up
                  </Button>
                </SignUpButton>
              </>
            )}
          </nav>
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-brand-parchment dark:bg-neutral-900 border-brand-accent/20 dark:border-neutral-700 w-[250px]">
                <div className="flex flex-col pt-16 space-y-4">
                  <MobileNavLink to="/app/workspace">Workspace</MobileNavLink>
                  <MobileNavLink to="/browse">Browse</MobileNavLink>
                  <MobileNavLink to="/app/library">My Library</MobileNavLink>
                  {/* Settings disabled - webhook config is admin-only */}
                  {/* <MobileNavLink to="/app/settings">Settings</MobileNavLink> */}
                  <MobileNavLink to="/pricing">Pricing</MobileNavLink>
                  <ThemeToggle className="self-start" />
                  <hr className="border-brand-accent/20" />
                  {isSignedIn ? (
                    <div className="flex items-center justify-start pl-2 relative z-[100]">
                      <UserButton
                        appearance={{
                          elements: {
                            userButtonPopoverCard: "z-[100]",
                            userButtonPopoverActionButton: "cursor-pointer",
                            userButtonPopoverFooter: "hidden"
                          }
                        }}
                        afterSignOutUrl="/"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-2 relative z-[60]">
                      <SignInButton mode="modal">
                        <Button variant="ghost" className="w-full justify-start text-brand-dark dark:text-amber-50 hover:bg-brand-accent/10 dark:hover:bg-neutral-800">
                          Login
                        </Button>
                      </SignInButton>
                      <SignUpButton mode="modal">
                        <Button className="w-full bg-brand-dark dark:bg-amber-600 text-brand-parchment dark:text-white hover:bg-brand-dark/90 dark:hover:bg-amber-700 rounded-full">
                          Sign Up
                        </Button>
                      </SignUpButton>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
};